import { Spin } from "antd";
import i18n from "i18next";
import type { FC } from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router";

import { useTerminalCopyMutation } from "src/app/features/Terminal/services/server/TerminalApi";
import type { TerminalCopyEntity } from "src/app/features/Terminal/types/TerminalCopyEntity";
import { FormManager } from "../../../../../components/Form/FormManager";
import { FormLayoutType } from "../../../../../components/Form/types";
import type { FormProps } from "../../../../../components/Form/types/FormProps";
import { showSuccessNotification } from "../../../../../components/NotificationAlerts";
import { ResourceName } from "../../../../../core/router/config/ResourceName";
import { BASE_PATH, FORM_ACTION } from "../../../../../core/router/types/routerTypes";
import type { FormJson } from "../../../../../core/types/FormJsonTypes";
import terminalCopyJson from "./terminal-copy.json";

export const TerminalCopyForm: FC<FormProps> = ({ runSubmit }) => {
    const formDefinition = terminalCopyJson as FormJson;
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { id } = useParams();

    const location = useLocation();
    const originalSiteId = location.state?.siteId ?? null;

    const [formData, setFormData] = useState<TerminalCopyEntity>({} as TerminalCopyEntity);

    const [saveFormData, { isLoading: saveFormIsInProgress }] = useTerminalCopyMutation();

    const copyContractsManuallyCheckedRef = useRef(false);

    useEffect(() => {
        setFormData((currentFormData) => ({
            ...currentFormData,
            orgUnitId: originalSiteId,
            copyContracts: true,
            copyContractsCheckReadOnly: true,
        }));
    }, [originalSiteId]);

    const onChangeCallback = useCallback(
        (nextFormData: TerminalCopyEntity) => {
            const isSelectedSiteSameAsOriginal =
                !!originalSiteId && !!nextFormData.orgUnitId && String(nextFormData.orgUnitId) === String(originalSiteId);

            const copyContractsChanged = nextFormData.copyContracts !== formData.copyContracts;

            if (!isSelectedSiteSameAsOriginal && copyContractsChanged) {
                copyContractsManuallyCheckedRef.current = !!nextFormData.copyContracts;
            }

            let resolvedCopyContracts = copyContractsManuallyCheckedRef.current;

            if (isSelectedSiteSameAsOriginal) {
                resolvedCopyContracts = true;
            } else if (copyContractsChanged) {
                resolvedCopyContracts = !!nextFormData.copyContracts;
            }

            setFormData({
                ...nextFormData,
                copyContracts: resolvedCopyContracts,
                copyContractsCheckReadOnly: isSelectedSiteSameAsOriginal,
            });
        },
        [formData.copyContracts, originalSiteId],
    );

    const onValidatedCallback = useCallback(
        async (valid: boolean) => {
            if (!valid || !formData) {
                return;
            }

            const result = await saveFormData({ data: formData });

            const hasError = "error" in result;
            if (!hasError) {
                dispatch(
                    showSuccessNotification({
                        text: i18n.t("common.formCopySuccess", {
                            id: result.data.id,
                            resource: "Terminal",
                        }),
                        autoCloseDisabled: false,
                    }),
                );
                navigate(`${BASE_PATH}/${ResourceName.TERMINALS}/${FORM_ACTION.EDIT}/${result.data.id}`);
            }
        },
        [saveFormData, formData, navigate, dispatch],
    );

    return (
        <Spin spinning={saveFormIsInProgress}>
            {formDefinition?.formConfiguration && (
                <FormManager<TerminalCopyEntity>
                    name="TerminalCopyForm"
                    formData={{ ...formData, id }}
                    formLayout={FormLayoutType.DEFAULT}
                    formDefinition={formDefinition.formConfiguration}
                    onChange={onChangeCallback}
                    shouldValidate={runSubmit}
                    onValidated={onValidatedCallback}
                />
            )}
        </Spin>
    );
};
